import type { AdminRouterContext } from '@/lib/router-context'
import { InfoCard, type InfoCardProps } from '@/components/contracts/InfoCard'
import OrderSourceBadge from '@/components/orders/OrderSourceBadge'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { EmptyState } from '@/components/patterns/empty-state'
import { Ledger, LedgerRow, LedgerSection } from '@/components/patterns/ledger'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePathHelper, type PathHelpers, type RouteParamValue } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  reportQueries,
  useExportTipsReportMutation,
  useTipsReport,
} from '@/lib/services/reports/reports.query'
import {
  getTipsReportFiltersFromSearch,
  tipsReportSearchSchema,
  tipsReportSortValues,
  tipsReportViewValues,
  type TipsReportFilters,
  type TipsReportSearch,
  type TipsReportView,
} from '@/lib/services/reports/reports.schema'
import type {
  TipsReport,
  TipsReportPerson,
  TipsReportRow,
  TipsReportSummary,
} from '@/lib/services/reports/reports.types'
import { cn, formatCurrency, parseCurrency } from '@/lib/utils'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import {
  ArrowDownWideNarrow,
  Clock3,
  Download,
  Flower2,
  HandCoins,
  History,
  ReceiptText,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'

type TipsSortOption = {
  value: TipsReportFilters['sort_by']
  label: string
  icon: LucideIcon
}

const SORT_OPTIONS: TipsSortOption[] = [
  {
    value: 'newest',
    label: 'Newest activity first',
    icon: Clock3,
  },
  {
    value: 'highest_tip',
    label: 'Highest tip first',
    icon: ArrowDownWideNarrow,
  },
  {
    value: 'oldest',
    label: 'Oldest activity first',
    icon: History,
  },
  {
    value: 'source_pos_first',
    label: 'By source, POS first',
    icon: ReceiptText,
  },
]

const VIEW_LABELS: Record<TipsReportView, string> = {
  orders: 'Orders',
  people: 'By Person',
}

export async function loadTipsReport({
  context,
  deps: { search },
  params,
}: {
  context: AdminRouterContext
  deps: { search: TipsReportSearch }
  params: { orgId: number }
}): Promise<void> {
  if (!context.ability.can('list', 'transactions')) throw notFound()

  if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
    return
  }

  const organization = await context.queryClient.ensureQueryData(
    organizationQueries.show(params.orgId)
  )
  const filters = getReportFilters(search, organization?.timezone)

  await context.queryClient.ensureQueryData(
    reportQueries.tips({
      organizationId: params.orgId,
      filters,
    })
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/reports/tips/')({
  component: TipsReportPage,
  validateSearch: (search) => tipsReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: loadTipsReport,
})

export function TipsReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization?.timezone)
  const { data: report } = useTipsReport(filters)
  const exportMutation = useExportTipsReportMutation()
  const reportNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    reportNow,
    organization?.timezone
  )
  // A shop that doesn't split tips (or a range before it did) sees no split tiles, columns or By Person tab.
  const showSplit = report?.rows.some((row) => row.tip_driver_percent !== null) ?? false
  const view = showSplit ? (search.view ?? 'orders') : 'orders'
  const hasRows = (report?.rows?.length ?? 0) > 0
  // Each control sets only its part of the URL; the dates, sort and tab carry over.
  const updateSearch = (changes: TipsReportSearch, replace = true) => {
    startTransition(() => {
      void navigate({
        ...path.tips.index().using({ orgId }).link,
        search: (prev) => ({ ...prev, ...changes }),
        replace,
      })
    })
  }
  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) =>
    updateSearch(
      { start_date, end_date, date_mode: date_mode ?? undefined },
      Boolean(search.start_date && search.end_date) || Boolean(search.date_mode)
    )
  const applySort = (sortBy: string) =>
    updateSearch({ sort_by: tipsReportSortValues.find((value) => value === sortBy) })
  const showView = (nextView: string) =>
    updateSearch({ view: nextView === 'people' ? 'people' : undefined })

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(
        response,
        `tips-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Unable to export the tips report right now.'))
    }
  }

  return (
    <div className="pb-6">
      <AdminPageHeader
        icon={HandCoins}
        title="Tips Report"
        className="mb-4 mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                ...filters,
                ...datePickerBounds,
              }}
              now={reportNow}
              onApply={applyFilters}
            />
            {/* Sorting only orders the order list. */}
            {view === 'orders' ? (
              <Select value={filters.sort_by} onValueChange={applySort}>
                <SelectTrigger aria-label="Sort tip activity" className="w-[230px]">
                  <SelectValue placeholder="Sort tip activity" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => {
                    const Icon = option.icon

                    return (
                      <SelectItem key={option.value} value={option.value}>
                        <Icon className="size-4" />
                        {option.label}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
            ) : null}
            <AdminHeaderActionButton
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={!hasRows || exportMutation.isPending}
            >
              <Download className="h-4 w-4" />
              Export Spreadsheet
            </AdminHeaderActionButton>
          </>
        }
      />

      <TipsReportResults
        report={report}
        showSplit={showSplit}
        view={view}
        onViewChange={showView}
      />
    </div>
  )
}

type TipsSummaryCard = Pick<InfoCardProps, 'icon' | 'tone' | 'title' | 'description' | 'value'> & {
  key: string
}

function TipsReportResults({
  report,
  showSplit,
  view,
  onViewChange,
}: {
  report: TipsReport | undefined
  showSplit: boolean
  view: TipsReportView
  onViewChange: (view: string) => void
}) {
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const summary: TipsReportSummary = report?.summary ?? {
    total_tips: '0.00',
    tips_on_account: '0.00',
    designer_tips: '0.00',
    driver_tips: '0.00',
    unsplit_tips: '0.00',
  }
  const rows = report?.rows ?? []
  const designers = report?.tips_by_person.designers ?? []
  const drivers = report?.tips_by_person.drivers ?? []

  const summaryCards: TipsSummaryCard[] = [
    {
      key: 'total_tips',
      title: 'Total tips',
      description: 'Booked on the order date',
      value: <ReportCurrencyValue amount={summary.total_tips} />,
      icon: HandCoins,
      tone: parseCurrency(summary.total_tips) < 0 ? 'destructive' : 'mint',
    },
    {
      key: 'tips_on_account',
      title: 'Tips on account',
      description: 'On orders not paid yet',
      value: <ReportCurrencyValue amount={summary.tips_on_account} />,
      icon: ReceiptText,
      tone: 'periwinkle',
    },
  ]

  // Designer, driver and not split add up to the total.
  const splitCards: TipsSummaryCard[] = [
    {
      key: 'designer_tips',
      title: 'Designer tips',
      description: "The designer's share",
      value: <ReportCurrencyValue amount={summary.designer_tips} />,
      icon: Flower2,
      tone: 'mint',
    },
    {
      key: 'driver_tips',
      title: 'Driver tips',
      description: "The driver's share of delivery tips",
      value: <ReportCurrencyValue amount={summary.driver_tips} />,
      icon: Truck,
      tone: 'periwinkle',
    },
    {
      key: 'unsplit_tips',
      title: 'Tips not split',
      description: 'Earlier orders, or with the split turned off',
      value: <ReportCurrencyValue amount={summary.unsplit_tips} />,
      icon: HandCoins,
      tone: 'neutral',
    },
  ]

  const ordersCard = (
    <Card>
      <CardHeader className="gap-1.5">
        <CardTitle>Order Tip Activity</CardTitle>
        <CardDescription>
          Tips booked and refunded in the selected date range, grouped by order.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length > 0 ? (
          <IndexDataTable
            mode="client"
            data={rows}
            columns={getTipsColumns({ path, orgId, showSplit })}
            getRowId={(row) => String(row.sales_order_id)}
            pagination={false}
          />
        ) : (
          <EmptyState
            size="compact"
            title="No tip activity found"
            description="Try a different date range, or wait for the first order with a tip."
          />
        )}
      </CardContent>
    </Card>
  )

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        {summaryCards.map((card) => (
          <InfoCard
            key={card.key}
            icon={card.icon}
            tone={card.tone}
            title={card.title}
            description={card.description}
            value={card.value}
          />
        ))}
      </div>

      {showSplit ? (
        <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {splitCards.map((card) => (
            <InfoCard
              key={card.key}
              icon={card.icon}
              tone={card.tone}
              title={card.title}
              description={card.description}
              value={card.value}
            />
          ))}
        </div>
      ) : null}

      {showSplit ? (
        <Tabs value={view} onValueChange={onViewChange} className="gap-4">
          <TabsList aria-label="Tips report view" layout="wrap" size="comfortable">
            {tipsReportViewValues.map((value) => (
              <TabsTrigger key={value} value={value}>
                {VIEW_LABELS[value]}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="orders">{ordersCard}</TabsContent>

          <TabsContent value="people">
            <Card>
              <CardHeader className="gap-1.5">
                <CardTitle>Tips by Person</CardTitle>
                <CardDescription>
                  Assign designers on orders and send routes to drivers from OneStem to see names
                  here.
                </CardDescription>
              </CardHeader>
              <CardContent className="@container">
                {designers.length > 0 || drivers.length > 0 ? (
                  <div className="grid gap-x-12 gap-y-6 @2xl:grid-cols-2">
                    <TipsByPersonList
                      title="Designers"
                      people={designers}
                      unassignedHint="Orders with no designer assigned"
                    />
                    <TipsByPersonList
                      title="Drivers"
                      people={drivers}
                      unassignedHint="Deliveries not sent out on a route"
                    />
                  </div>
                ) : (
                  <EmptyState
                    size="compact"
                    title="No split tips found"
                    description="Designer and driver tips show here once orders in this date range split a tip."
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        ordersCard
      )}
    </>
  )
}

function getTipsColumns({
  path,
  orgId,
  showSplit,
}: {
  path: PathHelpers
  orgId: RouteParamValue | undefined
  showSplit: boolean
}): ColumnDef<TipsReportRow>[] {
  const columns: ColumnDef<TipsReportRow>[] = [
    {
      accessorKey: 'sales_order_id',
      header: 'Order',
      meta: {
        label: 'Order',
        kind: 'id',
      },
      cell: ({ row }) => {
        const salesOrderId = row.original.sales_order_id

        return (
          <Link
            {...path.salesOrders.show(salesOrderId).using({ orgId }).link}
            className="font-medium underline-offset-2 hover:underline"
          >
            #{salesOrderId}
          </Link>
        )
      },
    },
    {
      accessorKey: 'activity_date',
      header: 'Activity Date',
      meta: {
        label: 'Activity Date',
        kind: 'date',
      },
      cell: ({ row }) => dayjs(row.original.activity_date).format('MMM D, YYYY'),
    },
    {
      accessorKey: 'source',
      header: 'Source',
      meta: {
        label: 'Source',
        kind: 'badge',
        align: 'center',
      },
      cell: ({ row }) => <OrderSourceBadge source={row.original.source} size="sm" />,
    },
    {
      accessorKey: 'purchaser_name',
      header: 'Purchaser',
      meta: {
        label: 'Purchaser',
        kind: 'primary',
      },
    },
    {
      accessorKey: 'order_total',
      header: 'Order Total',
      meta: {
        label: 'Order Total',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => <ReportCurrencyValue amount={row.original.order_total} />,
    },
    {
      accessorKey: 'tip_amount',
      header: 'Tip',
      meta: {
        label: 'Tip',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => <ReportCurrencyValue amount={row.original.tip_amount} />,
    },
    {
      accessorKey: 'refunded_tip_amount',
      header: 'Refunded Tip',
      meta: {
        label: 'Refunded Tip',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => (
        <ReportCurrencyValue amount={row.original.refunded_tip_amount} renderAsRefund />
      ),
    },
    {
      accessorKey: 'net_tip_amount',
      header: 'Net Tip',
      meta: {
        label: 'Net Tip',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => <ReportCurrencyValue amount={row.original.net_tip_amount} />,
    },
  ]
  const splitColumns: ColumnDef<TipsReportRow>[] = [
    {
      accessorKey: 'designer_tip_amount',
      header: 'Designer',
      meta: {
        label: 'Designer',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => (
        <SplitTipValue
          row={row.original}
          amount={row.original.designer_tip_amount}
          name={row.original.designer_name}
        />
      ),
    },
    {
      accessorKey: 'driver_tip_amount',
      header: 'Driver',
      meta: {
        label: 'Driver',
        kind: 'money',
        align: 'end',
      },
      cell: ({ row }) => (
        <SplitTipValue
          row={row.original}
          amount={row.original.driver_tip_amount}
          name={row.original.driver_name}
        />
      ),
    },
  ]

  return showSplit ? [...columns, ...splitColumns] : columns
}

// Orders that recorded no split show as such, never a guessed share. Who to pay goes beneath.
function SplitTipValue({
  row,
  amount,
  name,
}: {
  row: TipsReportRow
  amount: string
  name: string | null
}) {
  return (
    <span className="flex flex-col items-end">
      {row.tip_driver_percent === null ? (
        <span className="text-muted-foreground">Not split</span>
      ) : (
        <ReportCurrencyValue amount={amount} />
      )}
      {name ? (
        <Typography as="span" variant="caption" tone="muted">
          {name}
        </Typography>
      ) : null}
    </span>
  )
}

function TipsByPersonList({
  title,
  people,
  unassignedHint,
}: {
  title: string
  people: TipsReportPerson[]
  unassignedHint: string
}) {
  return (
    // Receipt width keeps a name near its amount when the list has half of a wide card.
    <Ledger className="max-w-md">
      <LedgerSection title={title} divided>
        {people.length > 0 ? (
          // Two designers can share a name, so a person's place in the list is their key.
          people.map((person, index) => {
            const amount = normalizeReportCurrencyAmount(person.tip_amount)

            return (
              <LedgerRow
                key={index}
                label={person.name ?? 'Not assigned'}
                hint={person.name ? undefined : unassignedHint}
                value={formatReportCurrency(amount)}
                valueTone={amount < 0 ? 'destructive' : 'default'}
              />
            )
          })
        ) : (
          <Typography as="p" variant="bodySmall" tone="muted" className="py-2">
            None in this date range.
          </Typography>
        )}
      </LedgerSection>
    </Ledger>
  )
}

function ReportCurrencyValue({
  amount,
  renderAsRefund = false,
}: {
  amount: string
  renderAsRefund?: boolean
}) {
  const normalizedAmount = normalizeReportCurrencyAmount(amount)
  const isNegative = normalizedAmount < 0 || (renderAsRefund && Math.abs(normalizedAmount) > 0)

  return (
    <span className={cn(isNegative && 'font-medium text-destructive')}>
      {formatReportCurrency(normalizedAmount, { renderAsRefund })}
    </span>
  )
}

function formatReportCurrency(
  amount: number,
  { renderAsRefund = false }: { renderAsRefund?: boolean } = {}
) {
  const normalizedAmount = normalizeReportCurrencyAmount(amount)

  if (normalizedAmount < 0 || (renderAsRefund && Math.abs(normalizedAmount) > 0)) {
    return `(${formatCurrency(Math.abs(normalizedAmount))})`
  }

  return formatCurrency(normalizedAmount)
}

function normalizeReportCurrencyAmount(amount: string | number) {
  return Math.round(parseCurrency(amount) * 100) / 100
}

function getReportFilters(
  search: TipsReportSearch,
  organizationTimezone: string
): TipsReportFilters {
  const { start_date, end_date, date_mode } = getCurrentReportDateFilters(
    getOrganizationNow(organizationTimezone)
  )
  const defaultFilters = { start_date, end_date, date_mode }

  return getTipsReportFiltersFromSearch(search, defaultFilters)
}
